import React from 'react';
import axios from 'axios';
import { Form, Header, Segment, Button, Input, Icon, Popup, Transition, Loader } from 'semantic-ui-react';
import { ToastContainer, toast } from 'react-toastify';

import PropertyFilter from './Property/PropertyFilter';
import PropertyDetails from './Property/PropertyDetails';
import PropertyTable from './Property/PropertyTable';
import PaginationSegment from './util/PaginationSegment';
import ResultsPerPageDropdown from './util/ResultsPerPageDropdown';
import { fetchOrganizationalUnits, fetchMunicipalities, fetchCantons, fetchEntities, fetchCountries, fetchRightTypes, fetchDisabledAccesses } from './Lookups/util';
import { IconHeader, FiltersOnStyle } from './util/css';
import { getTotalNumberOfPages, checkOrganizationalUnitsPrivilege, checkFaculty, checkWritePrivilege, formatListForDropdownOptions, DEFAULT_NUMBER_OF_ITEMS_PER_PAGE } from './util/util';

axios.defaults.xsrfHeaderName = "X-XSRF-TOKEN";
axios.defaults.xsrfCookieName = 'XSRF-TOKEN';
axios.defaults.withCredentials = true;
//axios.defaults.crossDomain = true;

class Property extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            organizationalUnits: [],
            organizationalUnitId: null,
            activeRole: null,

            organizationalUnitProperties: [],
            filteredOrganizationalUnitProperties: [],

            lookups: {
                municipalities: [],
                cantons: [],
                entities: [],
                countries: [],
                rightTypes: [],
                disabledAccesses: [],
            },
            allowedMunicipalities: [],

            // Form data - post
            street: '',
            placeName: '',
            municipality: null,
            canton: null,
            entity: null,
            country: null,
            propertyName: '',
            rightType: null,
            usefulSurface: '',
            teachingArea: '',
            disabledAccess: null,
            active: null,


            // Form data - put
            disabledMunicipality: false,
            disabledCanton: false,
            disabledEntity: false,

            organizationalUnitPropertyId: null,
            organizationalUnitPropertyOptLock: null,
            propertyId: null,
            propertyOptLock: null,
            addressId: null,
            addressOptLock: null,

            // Component behaviour
            requestMode: null,
            loading: true,
            showFiltersOnButton: false,
            showFilterMode: false,

            // For details
            details: false,
            organizationalUnitPropertyObject: null,

            // For required fields
            organizationalUnitError: false,
            streetError: false,
            placeError: false,
            countryError: false,
            propertyNameError: false,
            rightTypeError: false,
            usefulSurfaceError: false,
            teachingAreaError: false,
            municipalityError: false,
            cantonError: false,
            entityError: false,
            disabledAccessError: false,

            // For pagination
            activePage: 1,
            totalPages: 1,
            resultsPerPage: DEFAULT_NUMBER_OF_ITEMS_PER_PAGE,
            filterRequestData: null,

            // For sorting, default values
            fieldName: null,
            fieldProperty: null,
            direction: null,
        };

        this.sendPropertyRequest = this.sendPropertyRequest.bind(this);
        this.getOrganizationalUnitProperties = this.getOrganizationalUnitProperties.bind(this);
        this.updateOrganizationalUnitProperties = this.updateOrganizationalUnitProperties.bind(this);
        this.populateValues = this.populateValues.bind(this);
        this.resetValues = this.resetValues.bind(this);
        this.activateEditRequest = this.activateEditRequest.bind(this);
        this.activateAddRequest = this.activateAddRequest.bind(this);
        this.cancelRequest = this.cancelRequest.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);

        this.activateFilter = this.activateFilter.bind(this);
        this.onFilterData = this.onFilterData.bind(this);
        this.filtersOff = this.filtersOff.bind(this);
        this.getDataToRender = this.getDataToRender.bind(this);

        this.fetchLookups = this.fetchLookups.bind(this);
        this.getUserData = this.getUserData.bind(this);
        this.fetchDataForRole = this.fetchDataForRole.bind(this);

        this.switchToDetails = this.switchToDetails.bind(this);
        this.switchToTable = this.switchToTable.bind(this);
        this.handlePageChange = this.handlePageChange.bind(this);
        this.getFilteredOrganizationalUnitProperties = this.getFilteredOrganizationalUnitProperties.bind(this);
        this.handleResultsPerPageChange = this.handleResultsPerPageChange.bind(this);
        this.getSortedOrganizationalUnitProperties = this.getSortedOrganizationalUnitProperties.bind(this);

        this.handleMunicipalityChange = this.handleMunicipalityChange.bind(this);
        this.handleCountryChange = this.handleCountryChange.bind(this);
        this.handleEntityChange = this.handleEntityChange.bind(this);
        this.handleCantonChange = this.handleCantonChange.bind(this);
    }

    componentWillMount() {
        this.getUserData();
    }

    getUserData() {
        axios.all([this.fetchActiveRole(), this.fetchUserOrganizationalUnit()])
            .then(axios.spread((activeRoleRes, userOrganizationalUnitRes) => {
                const activeRole = activeRoleRes.data;
                const organizationalUnitId = userOrganizationalUnitRes.data;

                this.fetchLookups(activeRole, organizationalUnitId);
            }))
            .catch(err => {
                this.setState({ error: true, loading: false });
                console.log('Failed to fetch data.');
            });
    }

    fetchLookups(activeRole, organizationalUnitId) {
        axios.all([fetchMunicipalities(), fetchCantons(), fetchEntities(), fetchCountries(), fetchRightTypes(), fetchDisabledAccesses()])
            .then(axios.spread((municipalitiesRes, cantonsRes, entitiesRes, countriesRes, rightTypesRes, disabledAccessesRes) => {
                const municipalities = formatListForDropdownOptions(municipalitiesRes.data);
                const cantons = formatListForDropdownOptions(cantonsRes.data);
                const entities = formatListForDropdownOptions(entitiesRes.data);
                const countries = formatListForDropdownOptions(countriesRes.data);
                const rightTypes = formatListForDropdownOptions(rightTypesRes.data);
                const disabledAccesses = formatListForDropdownOptions(disabledAccessesRes.data);

                const lookups = {
                    municipalities,
                    cantons,
                    entities,
                    countries,
                    rightTypes,
                    disabledAccesses,
                };

                this.fetchDataForRole(activeRole, organizationalUnitId, lookups);
            }))
            .catch(err => {
                this.setState({ error: true, loading: false });
                console.log('Failed to fetch lookup data.', err);
            });
    }

    fetchDataForRole(activeRole, organizationalUnitId, lookups) {
        const allowedMunicipalities = [...lookups.municipalities];
        if (checkOrganizationalUnitsPrivilege(activeRole)) {
            axios.all([fetchOrganizationalUnits(), this.fetchAllOrganizationalUnitProperties()])
                .then(axios.spread((organizationalUnitsRes, oupRes) => {
                    const organizationalUnits = formatListForDropdownOptions(organizationalUnitsRes.data);
                    const organizationalUnitProperties = oupRes.data.responseList;

                    let totalPages = getTotalNumberOfPages(oupRes.data.total, DEFAULT_NUMBER_OF_ITEMS_PER_PAGE);
                    this.setState({
                        organizationalUnits,
                        organizationalUnitProperties,
                        loading: false,
                        totalPages,
                        lookups: { ...lookups, organizationalUnits },
                        activeRole,
                        organizationalUnitId,
                        allowedMunicipalities,
                    });
                }))
                .catch((err => {
                    this.setState({ error: true, loading: false });
                    console.log('Failed to fetch data.');
                }));
        } else if (checkFaculty(activeRole)) {
            this.fetchOrganizationalUnitProperties()
                .then((res) => {
                    const organizationalUnitProperties = res.data.responseList;

                    let totalPages = getTotalNumberOfPages(res.data.total, DEFAULT_NUMBER_OF_ITEMS_PER_PAGE);
                    this.setState({
                        organizationalUnitProperties,
                        loading: false,
                        totalPages,
                        lookups: { ...lookups },
                        activeRole,
                        organizationalUnitId,
                        allowedMunicipalities,
                    });
                })
                .catch((err) => {
                    console.log('Failed to fetch data: ', err);
                    this.setState({ error: true, loading: false });
                });
        }
    }

    fetchActiveRole() {
        return axios.get('/user/type');
    }

    fetchUserOrganizationalUnit() {
        return axios.get('/user/organizational-unit');
    }

    fetchOrganizationalUnitProperties(page = 0, resultsPerPage = DEFAULT_NUMBER_OF_ITEMS_PER_PAGE) {
        const pagination = {
            page,
            resultsPerPage,
        };

        return axios.post('/assets/organizational-unit-properties/paginated/', { ...pagination });
    }

    fetchAllOrganizationalUnitProperties(page = 0, resultsPerPage = DEFAULT_NUMBER_OF_ITEMS_PER_PAGE) {
        const pagination = {
            page,
            resultsPerPage,
        };

        return axios.post('/assets/organizational-unit-properties/all/', { ...pagination });
    }

    getOrganizationalUnitProperties(page, resultsPerPage, sortingDTO = null) {
        const {
            activeRole, fieldName, fieldProperty, direction
        } = this.state;
        const pagination = {
            page,
            resultsPerPage,
            fieldName: sortingDTO ? sortingDTO.fieldName : fieldName,
            fieldProperty: sortingDTO ? sortingDTO.fieldProperty : fieldProperty,
            order: sortingDTO ? sortingDTO.direction === 'ascending' : direction ? direction === 'ascending' : null,
        };

        if (checkOrganizationalUnitsPrivilege(activeRole)) {
            axios.post('/assets/organizational-unit-properties/all/', { ...pagination })
                .then(res => {
                    this.updateOrganizationalUnitProperties(page, resultsPerPage, res, sortingDTO);
                })
                .catch(err => {
                    toast('Došlo je do greške!');
                    console.log('Greska!');
                });
        } else {
            axios.post('/assets/organizational-unit-properties/paginated/', { ...pagination })
                .then(res => {
                    this.updateOrganizationalUnitProperties(page, resultsPerPage, res, sortingDTO);
                })
                .catch(err => {
                    toast('Došlo je do greške!');
                    console.log('Greska!');
                });
        }
    }

    getFilteredOrganizationalUnitProperties(page, resultsPerPage, sortingDTO = null) {
        const {
            fieldName, fieldProperty, direction
        } = this.state;
        const paginationDTO = {
            page,
            resultsPerPage,
            fieldName: sortingDTO ? sortingDTO.fieldName : fieldName,
            fieldProperty: sortingDTO ? sortingDTO.fieldProperty : fieldProperty,
            order: sortingDTO ? sortingDTO.direction === 'ascending' : direction ? direction === 'ascending' : null,
        };
        let { filterRequestData } = this.state;
        filterRequestData = { ...filterRequestData, paginationDTO };

        axios.post('/assets/organizational-unit-properties/filter/', { ...filterRequestData })
            .then(res => {
                this.updateOrganizationalUnitProperties(page, resultsPerPage, res, sortingDTO, true);
            })
            .catch(err => {
                console.log('Failed to fetch filter results');
                toast('Doslo je do greške prilikom filtriranja!');
            });
    }

    getSortedOrganizationalUnitProperties(fieldName, fieldProperty = null) {
        const sortingDTO = {
            direction: (this.state.fieldName === fieldName ? (this.state.direction === 'ascending' ? 'descending' : 'ascending') : 'ascending'),
            fieldName,
            fieldProperty
        };

        if (!this.state.showFiltersOnButton) {
            this.getOrganizationalUnitProperties(this.state.activePage - 1, this.state.resultsPerPage, sortingDTO);
        } else {
            this.getFilteredOrganizationalUnitProperties(this.state.activePage - 1, this.state.resultsPerPage, sortingDTO);
        }
    }

    updateOrganizationalUnitProperties(page, resultsPerPage, res, sortingDTO, filtersOn = false) {
        const organizationalUnitProperties = res.data.responseList;
        let totalPages = getTotalNumberOfPages(res.data.total, resultsPerPage);
        this.setState(prevState => ({
            activePage: page + 1,
            resultsPerPage,
            organizationalUnitProperties: filtersOn ? [] : organizationalUnitProperties,
            filteredOrganizationalUnitProperties: filtersOn ? organizationalUnitProperties : [],
            totalPages,
            fieldName: sortingDTO ? sortingDTO.fieldName : prevState.fieldName,
            fieldProperty: sortingDTO ? sortingDTO.fieldProperty : prevState.fieldProperty,
            direction: sortingDTO ? (sortingDTO.fieldName === prevState.fieldName ? (prevState.direction === 'ascending' ? 'descending' : 'ascending') : 'ascending') : prevState.direction,
        }));
    }

    onFilterData(propertyFilter) {
        const { activeRole, resultsPerPage } = this.state;
        let propertyFilterRequest = {
            ...propertyFilter,
            paginationDTO: {
                page: 0,
                resultsPerPage,
            },
        };

        // Making sure it is sending for the current logged in organizational unit
        if (checkFaculty(activeRole)) {
            propertyFilterRequest = {
                ...propertyFilter,
                organizationalUnit: this.state.organizationalUnitId
            };
        }

        axios.post('/assets/organizational-unit-properties/filter/', { ...propertyFilterRequest })
            .then(res => {
                const filterData = res.data.responseList;
                let totalPages = getTotalNumberOfPages(res.data.total, resultsPerPage);
                this.setState({
                    activePage: 1,
                    totalPages,
                    filteredOrganizationalUnitProperties: filterData,
                    organizationalUnitProperties: [],
                    showFiltersOnButton: true,
                    filterRequestData: propertyFilterRequest,
                    showFilterMode: false,
                });
            })
            .catch(err => {
                console.log('Failed to fetch filter results: ', err);
                toast('Došlo je do greške prilikom filtriranja!');
            });
    }

    getDataToRender() {
        if (this.state.showFiltersOnButton) {
            return this.state.filteredOrganizationalUnitProperties;
        }
        return this.state.organizationalUnitProperties;
    }

    sendPropertyRequest(
        organizationalUnitId, street, placeName, municipalityId,
        cantonId, entityId, countryId, propertyName, rightTypeId,
        usefulSurface, teachingArea, disabledAccessId, active,
        organizationalUnitPropertyId, organizationalUnitPropertyOptLock,
        propertyId, propertyOptLock
    ) {
        if (
            organizationalUnitId === null || street === null || street === '' || placeName === null || placeName === '' ||
            countryId === null || propertyName === null || propertyName === '' || rightTypeId === null ||
            usefulSurface === null || usefulSurface === '' || teachingArea === null || teachingArea === '' ||
            (cantonId === null && !this.state.disabledCanton) || (municipalityId === null && !this.state.disabledMunicipality) ||
            (entityId === null && !this.state.disabledEntity) || disabledAccessId === null
        ) {
            this.setState({
                organizationalUnitError: !organizationalUnitId,
                streetError: !street || street === '',
                placeError: !placeName || placeName === '',
                countryError: !countryId,
                propertyNameError: !propertyName || propertyName === '',
                rightTypeError: !rightTypeId,
                usefulSurfaceError: !usefulSurface || usefulSurface === '',
                teachingAreaError: !teachingArea || teachingArea === '',
                disabledAccessError: !disabledAccessId,
                municipalityError: municipalityId === null && !this.state.disabledMunicipality,
                cantonError: cantonId === null && !this.state.disabledCanton,
                entityError: entityId === null && !this.state.disabledEntity,
            });

            toast('Nisu sva potrebna polja popunjena!');
            return;
        }


        const propertyRequest = {
            organizationalUnit: { id: organizationalUnitId },
            property: {
                name: propertyName,
                address: {
                    street,
                    place: {
                        name: placeName,
                        municipality: municipalityId ? { id: municipalityId } : null,
                        canton: cantonId ? { id: cantonId } : null,
                        entity: entityId ? { id: entityId } : null,
                        country: countryId ? { id: countryId } : null,
                    }
                },
                disabledAccess: disabledAccessId ? { id: disabledAccessId } : null,
            },
            rightType: rightTypeId ? { id: rightTypeId } : null,
            usefulSurface,
            teachingArea,
            disabledAccess: disabledAccessId ? { id: disabledAccessId } : null,
            active: 1
        };

        if (this.state.requestMode === 'add') {
            axios.post('/assets/organizational-unit-properties/', { ...propertyRequest })
                .then(res => {
                    if (this.state.showFiltersOnButton) {
                        this.getFilteredOrganizationalUnitProperties(this.state.activePage - 1, this.state.resultsPerPage);
                    } else {
                        this.getOrganizationalUnitProperties(this.state.activePage - 1, this.state.resultsPerPage);
                    }
                    toast('Zahtjev uspješno poslan!');
                    this.resetValues(null);
                })
                .catch(err => {
                    console.log(err);
                    toast('Došlo je do greške prilikom dodavanja nepokretne imovine!');
                });
        } else if (this.state.requestMode === 'edit') {
            const propertyPutRequest = {
                id: organizationalUnitPropertyId,
                optLock: organizationalUnitPropertyOptLock,
                organizationalUnit: { id: organizationalUnitId },
                property: {
                    id: propertyId,
                    optLock: propertyOptLock,
                    name: propertyName,
                    address: {
                        street,
                        place: {
                            name: placeName,
                            municipality: municipalityId ? { id: municipalityId } : null,
                            canton: cantonId ? { id: cantonId } : null,
                            entity: entityId ? { id: entityId } : null,
                            country: countryId ? { id: countryId } : null,
                        }
                    },
                    disabledAccess: disabledAccessId ? { id: disabledAccessId } : null,
                },
                rightType: rightTypeId ? { id: rightTypeId } : null,
                usefulSurface,
                teachingArea,
                disabledAccess: disabledAccessId ? { id: disabledAccessId } : null,
                active: 1
            };
            axios.put('/assets/organizational-unit-properties/', { ...propertyPutRequest })
                .then(res => {
                    if (this.state.showFiltersOnButton) {
                        this.getFilteredOrganizationalUnitProperties(this.state.activePage - 1, this.state.resultsPerPage);
                    } else {
                        this.getOrganizationalUnitProperties(this.state.activePage - 1, this.state.resultsPerPage);
                    }
                    toast('Zahtjev uspješno uređen!');
                    this.resetValues(null);
                })
                .catch(err => {
                    console.log(err);
                    toast('Došlo je do greške prilikom uređivanja nepokretne imovine!');
                });
        }
    }

    activateAddRequest() {
        this.resetValues('add');
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }

    activateEditRequest(organizationalUnitProperty) {
        this.populateValues(organizationalUnitProperty);
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }

    cancelRequest() {
        this.resetValues(null);
    }

    activateFilter() {
        this.setState({ showFilterMode: !this.state.showFilterMode });
    }

    // TODO: Merge two setStates into one, figure out how
    filtersOff() {
        this.getOrganizationalUnitProperties(0, this.state.resultsPerPage);
        this.setState({
            showFilterMode: false, showFiltersOnButton: false,
        });
    }

    resetValues(requestMode) {
        const { activeRole } = this.state;
        this.setState(prevState => ({
            street: '',
            placeName: '',
            municipality: null,
            canton: null,
            entity: null,
            country: null,
            propertyName: '',
            rightType: null,
            usefulSurface: '',
            teachingArea: '',
            disabledAccess: null,
            active: null,
            allowedMunicipalities: this.state.lookups.municipalities,

            disabledMunicipality: false,
            disabledCanton: false,
            disabledEntity: false,
            organizationalUnitError: false,
            streetError: false,
            placeError: false,
            countryError: false,
            propertyNameError: false,
            rightTypeError: false,
            usefulSurfaceError: false,
            teachingAreaError: false,
            municipalityError: false,
            cantonError: false,
            entityError: false,
            disabledAccessError: false,

            organizationalUnitId: checkOrganizationalUnitsPrivilege(activeRole) ? null : prevState.organizationalUnitId,
            requestMode,
        }));
    }

    populateValues(organizationalUnitProperty) {
        if (organizationalUnitProperty) {
            const {
                street, place
            } = organizationalUnitProperty.property.address;
            this.setState({
                organizationalUnitId: organizationalUnitProperty.organizationalUnit.id,
                street,
                placeName: place.name,
                municipality: place.municipality ? place.municipality.id : null,
                canton: place.canton ? place.canton.id : null,
                entity: place.entity ? place.entity.id : null,
                country: place.country ? place.country.id : null,
                propertyName: organizationalUnitProperty.property.name,
                rightType: organizationalUnitProperty.rightType.id,
                usefulSurface: organizationalUnitProperty.usefulSurface,
                teachingArea: organizationalUnitProperty.teachingArea,
                disabledAccess: organizationalUnitProperty.disabledAccess ? organizationalUnitProperty.disabledAccess.id : null,

                organizationalUnitPropertyId: organizationalUnitProperty.id,
                organizationalUnitPropertyOptLock: organizationalUnitProperty.optLock,
                propertyId: organizationalUnitProperty.property.id,
                propertyOptLock: organizationalUnitProperty.property.optLock,

                disabledCanton: !place.canton,
                disabledEntity: !place.entity,
                disabledMunicipality: !place.municipality,

                requestMode: 'edit',
                organizationalUnitError: false,
                streetError: false,
                placeError: false,
                countryError: false,
                propertyNameError: false,
                rightTypeError: false,
                usefulSurfaceError: false,
                teachingAreaError: false,
                municipalityError: false,
                cantonError: false,
                entityError: false,
                disabledAccessError: false,
            });
        } else {
            this.setState({ requestMode: null });
        }
    }

    switchToDetails(organizationalUnitProperty) {
        console.log(organizationalUnitProperty);
        if (organizationalUnitProperty !== null) {
            this.setState({
                details: true,
                organizationalUnitPropertyObject: organizationalUnitProperty,
            });
        }
    }

    switchToTable() {
        this.setState({
            organizationalUnitPropertyObject: null,
            details: false,
        });
    }


    handleInputChange(event, { name, value }) {
        this.setState({
            [name]: value
        });
    }

    handleMunicipalityChange(e, { value }) {
        const municipality = this.state.lookups.municipalities.find(el => el.id === value);

        const canton = municipality.canton ? municipality.canton.id : null;
        const entity = municipality.entity ? municipality.entity.id : null;
        const country = municipality.country ? municipality.country.id : null;
        this.setState({
            municipality: value,
            canton,
            entity,
            country,
        });
    }

    handleEntityChange(e, { value }) {
        let {
            canton, municipality, disabledMunicipality, disabledCanton, allowedMunicipalities
        } = this.state;

        canton = null;
        municipality = null;
        const country = this.state.lookups.countries.find(el => el.name === 'Bosna i Hercegovina');

        if (value === 3) {
            disabledCanton = true;
            disabledMunicipality = true;
        } else if (value === 2) {
            disabledCanton = true;
            disabledMunicipality = false;
        } else {
            disabledCanton = false;
            disabledMunicipality = false;
        }

        allowedMunicipalities = this.state.lookups.municipalities.filter(el => el.entity && el.entity.id === value);

        this.setState({
            entity: value,
            canton,
            municipality,
            country: country.id,
            disabledCanton,
            disabledMunicipality,
            allowedMunicipalities
        });
    }

    handleCantonChange(e, { value }) {
        let { allowedMunicipalities } = this.state;
        allowedMunicipalities = this.state.lookups.municipalities.filter(el => el.canton && el.canton.id === value);
        const entity = this.state.lookups.entities.find(el => el.name === 'Federacija Bosne i Hercegovine');
        const country = this.state.lookups.countries.find(el => el.name === 'Bosna i Hercegovina');
        this.setState({
            allowedMunicipalities,
            canton: value,
            municipality: null,
            entity: entity.id,
            country: country.id,
        });
    }

    handleCountryChange(e, { value }) {
        let {
            disabledEntity, disabledCanton, disabledMunicipality
        } = this.state;

        if (value === 22) {
            disabledEntity = false;
            disabledCanton = false;
            disabledMunicipality = false;
        } else {
            disabledEntity = true;
            disabledCanton = true;
            disabledMunicipality = true;
        }

        this.setState({
            country: value,
            disabledEntity,
            disabledCanton,
            disabledMunicipality,
            municipality: null,
            entity: null,
            canton: null,
            allowedMunicipalities: this.state.lookups.municipalities,
        });
    }

    handlePageChange(e, { activePage }) {
        if (activePage === this.state.activePage) {
            return;
        }

        if (this.state.showFiltersOnButton) {
            this.getFilteredOrganizationalUnitProperties(activePage - 1, this.state.resultsPerPage);
        } else {
            this.getOrganizationalUnitProperties(activePage - 1, this.state.resultsPerPage);
        }
    }

    handleResultsPerPageChange(e, { value }) {
        if (this.state.showFiltersOnButton) {
            this.getFilteredOrganizationalUnitProperties(0, value);
            return;
        }

        this.getOrganizationalUnitProperties(0, value);
    }
    // NOTE: Couldn't extract the form and make another component VehicleForm, needs Redux for functionality
    render() {
        const {
            details,
            organizationalUnitPropertyObject,
            fieldName,
            direction,
            activeRole,
        } = this.state;
        if (this.state.error) return <div>Greška</div>;
        if (this.state.loading) return <div style={{ paddingTop: '100px', paddingBottom: '100px' }}><Loader active inline="centered" /></div>;
        if (details) {
            return (<PropertyDetails
                organizationalUnitProperty={organizationalUnitPropertyObject}
                switchToTable={this.switchToTable}
            />);
        }
        const organizationalUnitProperties = this.getDataToRender();
        const writePrivilege = checkWritePrivilege(activeRole);
        const organizationalUnitsPrivilege = checkOrganizationalUnitsPrivilege(activeRole);

        return (
            <div>
                <Header as="h1">Nepokretna imovina</Header>
                <Transition.Group animation="slide up" duration={1000}>
                    {(this.state.requestMode != null) &&
                    <Segment.Group raised>
                        <Segment raised>
                            <Header as="h3">
                                {this.state.requestMode === 'add' ? 'Dodavanje nove imovine' : 'Uređivanje imovine'}
                            </Header>
                        </Segment>
                        <Segment raised>
                            <Form noValidate>
                                <Header as="h4">Adresa</Header>

                                {organizationalUnitsPrivilege &&
                                <Form.Group widths="4">
                                    <Form.Dropdown clearable
                                        label="Organizaciona jedinica"
                                        placeholder="Organizaciona jedinica"
                                        value={this.state.organizationalUnitId}
                                        onChange={this.handleInputChange}
                                        name="organizationalUnitId"
                                        options={this.state.organizationalUnits}
                                        search
                                        fluid
                                        required
                                        error={this.state.organizationalUnitError}
                                        selection
                                    />
                                </Form.Group>
                                }
                                <Form.Group widths="equal">
                                    <Form.Field
                                        control={Input}
                                        label="Ulica"
                                        name="street"
                                        value={this.state.street}
                                        onChange={this.handleInputChange}
                                        placeholder="Unesite ulicu"
                                        required
                                        error={this.state.streetError}
                                    />
                                    <Form.Field
                                        control={Input}
                                        label="Mjesto"
                                        name="placeName"
                                        value={this.state.placeName}
                                        onChange={this.handleInputChange}
                                        placeholder="Unesite naziv mjesta"
                                        required
                                        error={this.state.placeError}
                                    />

                                    <Form.Dropdown clearable
                                        label="Opština"
                                        placeholder="Opština"
                                        value={this.state.municipality}
                                        name="municipality"
                                        onChange={this.handleMunicipalityChange}
                                        options={this.state.allowedMunicipalities}
                                        search
                                        fluid
                                        disabled={this.state.disabledMunicipality}
                                        required={!this.state.disabledMunicipality}
                                        error={this.state.municipalityError}
                                        selection
                                    />

                                    <Form.Dropdown clearable
                                        label="Kanton"
                                        placeholder="Kanton"
                                        value={this.state.canton}
                                        name="canton"
                                        onChange={this.handleCantonChange}
                                        options={this.state.lookups.cantons}
                                        search
                                        fluid
                                        disabled={this.state.disabledCanton}
                                        required={!this.state.disabledCanton}
                                        error={this.state.cantonError}
                                        selection
                                    />

                                    <Form.Dropdown clearable
                                        label="Entitet"
                                        placeholder="Entitet"
                                        value={this.state.entity}
                                        name="entity"
                                        onChange={this.handleEntityChange}
                                        options={this.state.lookups.entities}
                                        search
                                        fluid
                                        disabled={this.state.disabledEntity}
                                        required={!this.state.disabledEntity}
                                        error={this.state.entityError}
                                        selection
                                    />

                                    <Form.Dropdown clearable
                                        label="Država"
                                        placeholder="Država"
                                        value={this.state.country}
                                        name="country"
                                        onChange={this.handleCountryChange}
                                        options={this.state.lookups.countries}
                                        search
                                        fluid
                                        required
                                        error={this.state.countryError}
                                        selection
                                    />
                                </Form.Group>

                                <Form.Group widths="equal">
                                    <Form.Field
                                        control={Input}
                                        label="Naziv imovine"
                                        name="propertyName"
                                        value={this.state.propertyName}
                                        onChange={this.handleInputChange}
                                        placeholder="Naziv imovine"
                                        required
                                        error={this.state.propertyNameError}
                                    />

                                    <Form.Dropdown clearable
                                        label="Tip prava"
                                        placeholder="Tip prava"
                                        value={this.state.rightType}
                                        name="rightType"
                                        onChange={this.handleInputChange}
                                        options={this.state.lookups.rightTypes}
                                        search
                                        fluid
                                        required
                                        error={this.state.rightTypeError}
                                        selection
                                    />
                                    <Form.Field
                                        control={Input}
                                        label="Korisna površina (m²)"
                                        name="usefulSurface"
                                        type="number"
                                        value={this.state.usefulSurface}
                                        onChange={this.handleInputChange}
                                        placeholder="Korisna površina"
                                        required
                                        error={this.state.usefulSurfaceError}
                                    />

                                    <Form.Field
                                        control={Input}
                                        label="Površina za učenje (m²)"
                                        name="teachingArea"
                                        type="number"
                                        value={this.state.teachingArea}
                                        onChange={this.handleInputChange}
                                        placeholder="Površina za učenje"
                                        required
                                        error={this.state.teachingAreaError}
                                    />

                                    <Form.Dropdown clearable
                                        label="Pristup invalidima"
                                        placeholder="Pristup invalidima"
                                        value={this.state.disabledAccess}
                                        name="disabledAccess"
                                        onChange={this.handleInputChange}
                                        options={this.state.lookups.disabledAccesses}
                                        search
                                        fluid
                                        required
                                        error={this.state.disabledAccessError}
                                    />
                                </Form.Group>
                                <Button
                                    color="blue"
                                    onClick={
                                        () => {
                                            this.sendPropertyRequest(
                                                this.state.organizationalUnitId,
                                                this.state.street,
                                                this.state.placeName,
                                                this.state.municipality,
                                                this.state.canton,
                                                this.state.entity,
                                                this.state.country,
                                                this.state.propertyName,
                                                this.state.rightType,
                                                this.state.usefulSurface,
                                                this.state.teachingArea,
                                                this.state.disabledAccess,
                                                this.state.active,
                                                this.state.organizationalUnitPropertyId,
                                                this.state.organizationalUnitPropertyOptLock,
                                                this.state.propertyId,
                                                this.state.propertyOptLock,
                                            );
                                        }
                                    }
                                >
                                    {this.state.requestMode === 'add' ? 'Pošalji' : 'Uredi'}
                                </Button>
                                <Button
                                    onClick={this.cancelRequest}
                                    basic
                                    color="blue"
                                >Odustani</Button>
                            </Form>
                        </Segment>

                    </Segment.Group>}
                </Transition.Group>
                <Segment.Group raised>
                    <Segment>
                        <Header as="h3">
                            Spisak nepokretne imovine
                            <Header style={IconHeader} floated="right" >
                                {this.state.showFiltersOnButton &&
                                    <Button
                                        floated="left"
                                        icon
                                        labelPosition="right"
                                        color="orange"
                                        size="small"
                                        style={FiltersOnStyle}
                                    >
                                        <Icon className="remove" onClick={this.filtersOff} />
                                    Filter uključen
                                    </Button>}
                                <Icon />
                                <Popup
                                    trigger={<Icon link circular basic floated="right" name="filter" onClick={this.activateFilter} />}
                                    content="Filtriraj podatke"
                                    hideOnScroll
                                    position="top center"
                                />
                            </Header>
                        </Header>
                    </Segment>
                    {this.state.showFilterMode &&
                        <PropertyFilter
                            onFilterData={this.onFilterData}
                            lookups={this.state.lookups}
                            organizationalUnits={this.state.organizationalUnits}
                            organizationalUnitsPrivilege={organizationalUnitsPrivilege}
                            cancelFilter={this.activateFilter}
                        />
                    }
                    <Segment>
                        {writePrivilege &&
                            <React.Fragment>
                                <Button
                                    floated="left"
                                    icon
                                    labelPosition="left"
                                    primary
                                    size="small"
                                    onClick={this.activateAddRequest}
                                >
                                    <Icon className="file text outline icon" />
                                Dodavanje nove imovine
                                </Button>
                                <ResultsPerPageDropdown
                                    resultsPerPage={this.state.resultsPerPage}
                                    onResultsPerPageChange={this.handleResultsPerPageChange}
                                />
                                <br />
                                <br />
                            </React.Fragment>
                        }
                        {organizationalUnitProperties && organizationalUnitProperties.length ?
                            <PropertyTable
                                organizationalUnitProperties={organizationalUnitProperties}
                                activateEditRequest={this.activateEditRequest}
                                writePrivilege={writePrivilege}
                                switchToDetails={this.switchToDetails}
                                getSortedOrganizationalUnitProperties={this.getSortedOrganizationalUnitProperties}
                                fieldName={fieldName}
                                direction={direction}
                            /> :
                            <p>Nema podataka</p>
                        }
                    </Segment>
                    <PaginationSegment
                        activePage={this.state.activePage}
                        totalPages={this.state.totalPages}
                        onPageChange={this.handlePageChange}
                    />
                    <ToastContainer hideProgressBar />
                </Segment.Group>
            </div>
        );
    }
}


export default Property;



// WEBPACK FOOTER //
// ./src/views/Assets/Property.js